import React, { Component } from 'react';
import { arrayOf, bool, date, number, object, oneOfType, shape, string, func } from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import qs from 'qs';
import InPlaceIndicator from 'ps-components/LoadingIndicator/InPlaceIndicator/InPlaceIndicator';
import InternalData from '../InternalData/InternalData';
import InternalJobDescription from '../InternalJobDescription/InternalJobDescription';
import MarketRange from '../MarketRange/MarketRange';
import OverallComments from '../OverallComments/OverallComments';
import CompensationSummaryPricingDetails from '../PricingDetails/CompensationSummaryPricingDetails';
import CompensationSummaryParticipationMatchDetails from '../ParticipationMatchDetails/CompensationSummaryParticipationMatchDetails';
import SurveyJobDescription from '../SurveyJobDescription/SurveyJobDescription';
import EmployeeData from '../EmployeeData/EmployeeData';
import PdfCoverPage from './PdfCoverPage/PdfCoverPage';
import { setSelectedJob } from '../../JobMatching/OrgJobsActions';
import * as compensationSummaryActionCreators from '../CompensationSummaryActions';
import { compensationSummaryJobPricingGridActions } from '../../MarketPricing/JobPricingDetails/JobPricingGrid/CompensationSummaryJobPricingGridCreate';
import './CompensationSummaryPdf.scss';
import { PRICING_TYPE } from '../../SkillGeoDifferential/SkillGeoDifferentialReducer';
import { getColumnIndex, getColumnValue } from 'ps-components/AdvancedTable/GenericGrid/GenericGridHelper';
import { isAdHocJobId } from '../../MarketPricing/helper';
import * as participationMatchesActions from '../../MarketPricing/ParticipationMatches/ParticipationMatchesActions';
import { participationMatchesGridActions } from '../../MarketPricing/ParticipationMatches/ParticipationMatchesGrid/ParticipationMatchesGridCreate';

class CompensationSummaryPdf extends Component {
  static propTypes = {
    data: shape({
      employeeData: object,
      internalData: arrayOf(
        shape({
          key: string,
          value: oneOfType([number, string])
        })
      ),
      marketRangeData: shape({
        '25th': number,
        '50th': number,
        '75th': number
      }),
      slottedJobs: arrayOf(
        shape({
          slotJobCode: string,
          slotJobTitle: string,
          slotJobCodeKey: string
        })
      ),
      pricingType: string,
      selectedJobs: arrayOf(
        shape({
          id: number,
          code: string,
          codeKey: string
        })
      )
    }),
    organization: shape({
      clientManager: string,
      code: string,
      defaultGroup: shape({
        id: string,
        name: string
      }),
      id: string,
      implemenationManager: string,
      logo: string,
      name: string,
      useJobCodeKey: bool
    }),
    replacementTokens: arrayOf(
      shape({
        token: string,
        value: string
      })
    ),
    selectedJob: shape({
      code: string,
      codeKey: string,
      description: string,
      id: string,
      title: string
    }),
    match: shape({
      params: shape({
        id: string
      })
    }),
    showEmployeeData: bool,
    showInternalData: bool,
    showInternalJobDescription: bool,
    showLastModified: bool,
    showMarketComparison: bool,
    showMarketRange: bool,
    showMatchDetails: bool,
    showOverallComments: bool,
    showOverallComposites: bool,
    showSurveyJobDescription: bool,
    showCoverPage: bool,
    actions: object,
    group: object,
    loading: bool,
    showJobCodeKey: bool,
    monetaryRounding: number,
    percentRounding: number,
    numericRounding: number,
    selectedBenchmarkYear: number,
    location: object,
    displayIn: string,
    jobPricingGridActions: object,
    jobPricing: object,
    jobPricingGrid: object,
    participationMatches: object,
    participationMatchesActions: object,
    participationMatchesGridActions: object,
    showParticipationMatchesWithData: bool,
    showParticipationMatchesWithoutData: bool,
    siteSettings: shape({
      showAgingFactor: bool,
      showWgtBy: bool,
      useParticipationMatch: bool,
      showGeoAdjustment: bool
    }),
    viewName: string,
    surveyViewName: string,
    selectedEmployeesViewPk: string,
    selectedJobDetailsViewPk: string
  };

  componentDidMount() {
    const { match } = this.props;
    const {
      setSelectedJob,
      setDisplayIn,
      setViewName,
      setSurveyViewName,
      setSelectedEmployeesViewPk,
      setSelectedJobDetailsViewPk,
      setShowZeroWeightMatches
    } = this.props.actions;
    const queryString = qs.parse(location.search, { ignoreQueryPrefix: true });
    const displayIn = queryString.displayIn ? queryString.displayIn : 'annual';
    setDisplayIn(displayIn);

    const viewName = queryString.viewName ? queryString.viewName : null;
    setViewName(viewName);

    const surveyViewName =
      queryString.surveyViewName && queryString.surveyViewName !== 'null' ? queryString.surveyViewName : undefined;
    setSurveyViewName(surveyViewName);

    const selectedEmployeesViewPk =
      queryString.selectedEmployeesViewPk && queryString.selectedEmployeesViewPk !== 'null'
        ? queryString.selectedEmployeesViewPk
        : undefined;
    setSelectedEmployeesViewPk(selectedEmployeesViewPk);

    const showZeroWeightMatches = queryString.showZeroWeightMatches !== 'false' || queryString.showZeroWeightMatches === 'null' || !queryString.showZeroWeightMatches
    setShowZeroWeightMatches(showZeroWeightMatches)

    const selectedJobDetailsViewPk =
      queryString.selectedJobDetailsViewPk && queryString.selectedJobDetailsViewPk !== 'null'
        ? queryString.selectedJobDetailsViewPk
        : undefined;
    setSelectedJobDetailsViewPk(selectedJobDetailsViewPk);

    setSelectedJob(match.params);
  }

  componentDidUpdate(prevProps) {
    const {
      actions,
      selectedJob,
      replacementTokens,
      location,
      displayIn,
      jobPricingGridActions,
      organization,
      viewName,
      surveyViewName,
      participationMatchesGridActions,
      selectedEmployeesViewPk,
      selectedJobDetailsViewPk,
      userRights,
      showZeroWeightMatches
    } = this.props;
    const hasSelectedJob = Object.keys(selectedJob).length > 1;

    if (
      hasSelectedJob &&
      selectedJob.code !== prevProps.selectedJob.code &&
      selectedJob.codeKey !== prevProps.selectedJob.codeKey
    ) {
      const queryString = qs.parse(location.search, { ignoreQueryPrefix: true });
      const selectedBenchmarkYear = queryString.benchmarkYear ? queryString.benchmarkYear : null;
      actions.initCompSum(
        selectedJob,
        replacementTokens,
        selectedBenchmarkYear,
        displayIn,
        jobPricingGridActions.search,
        organization.masterAccountId,
        selectedJob?.currency || 'USD',
        viewName,
        surveyViewName,
        participationMatchesGridActions.search,
        selectedEmployeesViewPk,
        selectedJobDetailsViewPk,
        userRights.restrictedUser,
        showZeroWeightMatches
      );
    }
  }

  render() {
    const {
      customHeader,
      data,
      displayIn,
      group,
      jobPricing,
      jobPricingGrid,
      jobPricingGridActions,
      loading,
      monetaryRounding,
      numericRounding,
      organization,
      participationMatches,
      participationMatchesActions,
      participationMatchesGridActions,
      percentRounding,
      replacementTokens,
      reportName,
      selectedJob,
      showCoverPage,
      showEmployeeData,
      showInternalData,
      showInternalJobDescription,
      showJobCodeKey,
      showLastModified,
      showMarketComparison,
      showMarketRange,
      showMatchDetails,
      showOverallComments,
      showOverallComposites,
      showParticipationMatchesWithData,
      showParticipationMatchesWithoutData,
      showPricingDetails,
      showSurveyJobDescription,
      siteSettings,
      surveyViewName,
      userRights
    } = this.props;

    const pmMatches = participationMatches?.participationMatchesReducer?.surveyJobs?.jobs;
    const pmCountWithData = pmMatches?.filter(pm => pm.hasData);
    const pmCountWithoutData = pmMatches?.filter(pm => !pm.hasData);
    const showPMWithData = pmCountWithData?.length === 0 ? false : showParticipationMatchesWithData;
    const showPMWithoutData = pmCountWithoutData?.length === 0 ? false : showParticipationMatchesWithoutData;

    const isPageLoading =
      loading || Object.keys(selectedJob) < 1 || participationMatches?.participationMatchesReducer?.surveyJobs?.loading;

    if (isPageLoading) {
      return <InPlaceIndicator />;
    }

    const pricingType = jobPricingGrid?.metadata.pricingType;
    const isJobPriced = pricingType === PRICING_TYPE.PRICED;
    const isJobSlotted = pricingType === PRICING_TYPE.SLOTTED;
    const surveyWeightIndex = getColumnIndex(jobPricingGrid, 'data_weighting_factor');
    const benchmarkAdjustment = getColumnValue(jobPricingGrid, 'benchmark_adjustment_default');
    const benchmarkComment = getColumnValue(jobPricingGrid, 'benchmark_comment_default');
    const areAllWeightsZero =
      surveyWeightIndex !== -1 && jobPricingGrid.data.every(survey => survey[surveyWeightIndex] === 0);
    const isMatchedAndWeightedZero = (!benchmarkAdjustment || areAllWeightsZero) && jobPricingGrid.data.length >= 1;
    const hasComment = benchmarkComment !== null && benchmarkComment.length > 0;
    const hasAdjustment = benchmarkAdjustment !== null && benchmarkAdjustment !== 1;
    const shouldShowEmployeeData =
      showEmployeeData &&
      !userRights.restrictedUser &&
      data.employeeData &&
      data.employeeData.rows &&
      data.employeeData.rows.length > 0;

    if (selectedJob && selectedJob.title) {
      let title = selectedJob.code + ' ';
      if (organization.useJobCodeKey && selectedJob.codeKey) {
        title += selectedJob.codeKey + ' ';
      }
      title += selectedJob.title;
      document.title = title;
    } else if (selectedJob && isAdHocJobId(selectedJob.id)) {
      document.title = 'Ad Hoc pricing';
    }

    const lastModified = getColumnValue(jobPricingGrid, 'last_modified');
    const lastModifiedUserName = getColumnValue(jobPricingGrid, 'last_mod_user_name');
    const agingDate = getColumnValue(jobPricingGrid, 'aging_date');

    let coverPage = null;
    if (showCoverPage) {
      coverPage = (
        <div className="comp-sum-content__pdf-cover-page" id="pdfCoverPage">
          <div />
          <PdfCoverPage
            customHeader={customHeader}
            lastModifiedDate={lastModified}
            lastModifiedName={lastModifiedUserName}
            dataAgedToDate={agingDate}
            reportName={reportName}
            selectedJob={selectedJob}
            showLastModified={showLastModified}
          />
          <div />
        </div>
      );
    }

    const queryString = qs.parse(location.search, { ignoreQueryPrefix: true });

    return (
      <div className="comp-sum-pdf" id="comp-sum-pdf">
        <div className="comp-sum-content__pdf-page-content">
          {coverPage}
          {showInternalData && !isAdHocJobId(selectedJob.id) && (
            <InternalData
              checked={showInternalData}
              data={data.internalData}
              replacementTokens={replacementTokens}
              hasData={!isAdHocJobId(selectedJob.id)}
            />
          )}
          {showInternalJobDescription && selectedJob.description && selectedJob.description.length > 0 && (
            <InternalJobDescription checked={showInternalJobDescription} data={selectedJob.description} />
          )}
          {showMarketRange &&
            !userRights.restrictedUser &&
            !isAdHocJobId(selectedJob.id) &&
            Object.values(data.marketRange).length > 0 &&
            (isJobPriced || isJobSlotted) &&
            !isMatchedAndWeightedZero && (
              <MarketRange
                checked={showMarketRange}
                id="market-range-chart__pdf"
                data={data.marketRange}
                displayIn={displayIn}
                monetaryRounding={monetaryRounding}
              />
            )}
          {showPricingDetails && (isJobPriced || isJobSlotted) && (
            <CompensationSummaryPricingDetails
              siteSettings={siteSettings}
              checked={showPricingDetails}
              showMarketComparison={showMarketComparison && !userRights.restrictedUser}
              showMatchDetails={showMatchDetails}
              showOverallComposites={showOverallComposites}
              hasEmployeeDataRows={data?.employeeData?.rowCount > 0}
              jobPricingGrid={jobPricingGrid}
              jobPricingGridActions={jobPricingGridActions}
              jobPricing={jobPricing}
              job={selectedJob}
              benchmarkYear={queryString.benchmarkYear}
              displayIn={displayIn}
              masterAccountId={organization.masterAccountId}
              aging={jobPricing?.selectedAgingFactor}
              loading={loading}
              selectedViewPk={jobPricing.selectedViewPk}
              useJobCodeKey={organization.useJobCodeKey}
              isPrint={true}
            />
          )}
          {showOverallComments && (hasComment || hasAdjustment) && (isJobPriced || isJobSlotted) && (
            <OverallComments
              checked={showOverallComments}
              compositeAdjustment={benchmarkAdjustment}
              compositeComment={benchmarkComment}
            />
          )}
          {showSurveyJobDescription && isJobPriced && (
            <SurveyJobDescription checked={showSurveyJobDescription} data={jobPricingGrid} />
          )}
          {(showPMWithData || showPMWithoutData) && (isJobPriced || isJobSlotted) && (
            <CompensationSummaryParticipationMatchDetails
              actions={{
                ...participationMatchesActions,
                ...participationMatchesGridActions
              }}
              benchmarkYear={queryString.benchmarkYear}
              counts={{ data: 0, noData: 0 }}
              currency={selectedJob?.currency || 'USD'}
              displayIn={displayIn}
              filterText={''}
              grid={participationMatches.participationMatchesGridReducer}
              groupId={group.groupId}
              hideSelect={true}
              isPrint={true}
              job={selectedJob}
              jobId={selectedJob.id}
              masterAccountId={organization.masterAccountId}
              monetaryRounding={monetaryRounding}
              numericRounding={numericRounding}
              percentRounding={percentRounding}
              selectedSurveys={[]}
              selectedViewPk={jobPricing.selectedViewPk}
              showParticipationMatchesWithData={showPMWithData}
              showParticipationMatchesWithoutData={showPMWithoutData}
              showWithData={true}
              surveyJobs={participationMatches.participationMatchesReducer.surveyJobs}
              surveyViewName={surveyViewName}
            />
          )}
          {shouldShowEmployeeData && (
            <EmployeeData
              checked={showEmployeeData}
              data={data.employeeData}
              replacementTokens={replacementTokens}
              showJobCodeKey={showJobCodeKey}
              monetaryRounding={monetaryRounding}
              percentRounding={percentRounding}
              numericRounding={numericRounding}
            />
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ compensationSummary, session, orgJobs, marketPricing, group }) => {
  return {
    ...compensationSummary,
    group,
    organization: session.organization,
    replacementTokens: session.replacementTokens,
    selectedJob: orgJobs.selectedJob,
    jobPricing: marketPricing.JobPricingReducer,
    jobPricingGrid: marketPricing.JobPricingReducer.CompensationSummaryGridReducer,
    siteSettings: {
      showAgingFactor: compensationSummary.showAgingFactor,
      showWgtBy: compensationSummary.showWgtBy,
      useParticipationMatch: compensationSummary.useParticipationMatch,
      showGeoAdjustment: compensationSummary.showGeoAdjustment
    },
    participationMatches: marketPricing.ParticipationMatchesReducer,
    userRights: session.user.rights
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ ...compensationSummaryActionCreators, setSelectedJob }, dispatch),
  jobPricingGridActions: bindActionCreators(
    {
      ...compensationSummaryJobPricingGridActions
    },
    dispatch
  ),
  participationMatchesActions: bindActionCreators(
    {
      ...participationMatchesActions
    },
    dispatch
  ),
  participationMatchesGridActions: bindActionCreators(
    {
      ...participationMatchesGridActions
    },
    dispatch
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(CompensationSummaryPdf);
